import { transition, trigger, useAnimation } from "@angular/animations";
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { Subscription } from "rxjs";
import { distinctUntilChanged, filter } from "rxjs/operators";

import {
    ProductFinishVariant,
    ProductSizeVariant,
    ProductStrapVariant,
    ProductVariants,
} from "@hermes/api-model-product";
import { Settings } from "@hermes/app-core";
import { PRODUCT_PAGE_IMAGE_SIZE } from "@hermes/fragments/product-utils";
import { ProductPageFacade } from "@hermes/states/product-page";
import { formatSkuForImage } from "@hermes/utils-generic/helpers";

import {
    nextGuizmoAnim,
    nextStrapAnim,
    previousGuizmoAnim,
    previousStrapAnim,
} from "./apple-watch-image.animations";

/** Object contains new skus from state */
export interface ApwSkusComposite {
    guizmoSku: string;
    strapSku: string;
}

/** Type of variant toggle expanded */
export type ApwVariantChangeType =
    | "sizes"
    | "finishes"
    | "straps"
    | "strapSizes";

/** Directions possible for animation */
export type ApwAnimationDirectionType = "previous" | "next";

/**
 * This components manage animations for each variants changes on APW template
 * It's not use @input() only requires from state :
 * - guizmo / strap skus,
 * - which variation changes
 * - variants arrays
 */
@Component({
    selector: "h-apple-watch-image-animations",
    templateUrl: "./apple-watch-image-animations.component.html",
    styleUrls: ["./apple-watch-image-animations.component.scss"],
    animations: [
        /**
         * Triggered by a change of size or finish :
         * - the strap disappear with fade out
         * - the old guizmo disappear with a slide effect then fade out
         * - the new guizmo appear like a slide and fade in.
         * - the strap appear with fade in
         */
        trigger("guizmoAnimation", [
            transition("void => next", [useAnimation(nextGuizmoAnim)]),
            transition("void => previous", [useAnimation(previousGuizmoAnim)]),
        ]),
        /**
         * Triggered by a change of strap :
         * - the old strap disappear with fade out
         * - the new strap appear with fade in and slide
         */
        trigger("strapAnimation", [
            transition("void => next", [useAnimation(nextStrapAnim)]),
            transition("void => previous", [useAnimation(previousStrapAnim)]),
        ]),
    ],
})
export class AppleWatchImageAnimationsComponent implements OnInit, OnDestroy {
    @Input() compositeAsset: string;

    /** Sku's of current APW product composite */
    public currentGuizmoSku: string;
    public currentStrapSku: string;

    /** Sku's of next APW product composite */
    public nextGuizmoSku: string;
    public nextStrapSku: string;

    /** State of apw animations */
    public guizmoAnimationState: ApwAnimationDirectionType | "void" = "void";
    public strapAnimationState: ApwAnimationDirectionType | "void" = "void";

    /** Variant animatianimation trigger */
    public variantChangeType: ApwVariantChangeType;

    /** Variants of current product */
    public variants: ProductVariants;

    /** Subscription array of observable stream */
    public subscription: Subscription = new Subscription();

    public size = PRODUCT_PAGE_IMAGE_SIZE;

    constructor(
        private settings: Settings,
        private changeDetector: ChangeDetectorRef,
        private productPageFacade: ProductPageFacade,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.productPageFacade.aPWskus$
                .pipe(
                    distinctUntilChanged((previous, next) =>
                        this.isSameCompositeSkus(previous, next),
                    ),
                )
                .subscribe((skus: ApwSkusComposite) => {
                    if (this.isInitialFetch()) {
                        this.currentGuizmoSku = skus.guizmoSku;
                        this.currentStrapSku = skus.strapSku;
                    } else {
                        this.nextGuizmoSku = skus.guizmoSku;
                        this.nextStrapSku = skus.strapSku;
                        this.startAnimation();
                    }
                    this.changeDetector.detectChanges();
                }),
        );
        this.subscription.add(
            this.productPageFacade.variantChangeType$
                .pipe(filter((type) => type !== undefined))
                .subscribe((type: ApwVariantChangeType) => {
                    this.variantChangeType = type;
                }),
        );
        this.subscription.add(
            this.productPageFacade.variants$.subscribe(
                (variants: ProductVariants) => {
                    this.variants = variants;
                },
            ),
        );
    }

    /**
     * Return true if it's the first apw product fetching
     */
    public isInitialFetch(): boolean {
        return !this.currentGuizmoSku && !this.currentStrapSku;
    }

    /**
     * Compare previous skus value with next
     * We dont want trigger on stream if there is no changes
     * It could trigger unwanted animation
     * Return false if guizmo or strap sku change
     */
    public isSameCompositeSkus(
        previous: ApwSkusComposite,
        next: ApwSkusComposite,
    ): boolean {
        return (
            previous.guizmoSku === next.guizmoSku &&
            previous.strapSku === next.strapSku
        );
    }

    /**
     * Start animation according to current animation type
     */
    public startAnimation(): void {
        if (
            this.variantChangeType === "sizes" ||
            this.variantChangeType === "finishes"
        ) {
            this.guizmoAnimationState = this.getAnimationDirection();
        }
        if (this.variantChangeType === "straps") {
            this.strapAnimationState = this.getAnimationDirection();
        }
    }

    /**
     * Compare current and next variant index into variants array to get direction of animation
     */
    public getAnimationDirection(): ApwAnimationDirectionType {
        const variants: any[] = this.variants[this.variantChangeType];
        let currentIndex: number;
        let nextIndex: number;
        if (
            this.variantChangeType === "sizes" ||
            this.variantChangeType === "finishes"
        ) {
            currentIndex = variants.findIndex(
                (item: ProductFinishVariant | ProductSizeVariant) =>
                    item.sku.split("-")[1] === this.currentGuizmoSku,
            );
            nextIndex = variants.findIndex(
                (item: ProductFinishVariant | ProductSizeVariant) =>
                    item.sku.split("-")[1] === this.nextGuizmoSku,
            );
        }
        if (this.variantChangeType === "straps") {
            currentIndex = variants.findIndex(
                (item: ProductStrapVariant) =>
                    item.itemSku === this.currentStrapSku,
            );
            nextIndex = variants.findIndex(
                (item: ProductStrapVariant) =>
                    item.itemSku === this.nextStrapSku,
            );
        }

        if (currentIndex > nextIndex) {
            return "previous";
        }
        return "next";
    }

    /**
     * Trigger on each animation end
     * Reset state to prepare next animation
     */
    public animationDone(_event: AnimationEvent, type: string): void {
        if (type === "guizmo" && this.nextGuizmoSku) {
            this.currentGuizmoSku = this.nextGuizmoSku;
            this.currentStrapSku = this.nextStrapSku;
            this.nextGuizmoSku = undefined;
            this.nextStrapSku = undefined;
            this.guizmoAnimationState = "void";
        }
        if (type === "strap" && this.nextStrapSku) {
            this.currentGuizmoSku = this.nextGuizmoSku;
            this.currentStrapSku = this.nextStrapSku;
            this.nextGuizmoSku = undefined;
            this.nextStrapSku = undefined;
            this.strapAnimationState = "void";
        }
    }

    /**
     * Retun the image url for composite product
     * - transparant background
     * - sku encoded for space
     * - size for all breakpoint
     */
    public getImageUrl(sku: string, size: number): string {
        if (this.compositeAsset) {
            return `${this.settings.assetsUrlProduct}${encodeURIComponent(
                formatSkuForImage(sku),
            )}_${this.compositeAsset}?fmt=png-alpha&wid=${size}&hei=${size}`;
        }

        return "";
    }

    /**
     * Unsubscribe from all observable stream to avoid memory leaks
     */
    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
